// src/pages/ProductionToolkit/utils/batchNumberUtils.js
//
// Atomic batch number counters keyed by (TYPE, SKU_ROOT, YYYYMMDD).
// Examples:
//   INGR-SA-SSSG-20250812-001
//   PROD-CH-CHOR-20250812-017
//
// Public API (existing):
//   deriveSkuRoot(codeLike) -> string
//   getNextInternalBatchNo(nameOrCode, skuLike, dateYmd?) -> string
//   getNextProductBatchNo(productCode, skuLike, dateYmd?) -> string
//   getNextBatchNo(typePrefix, codeLike, dateYmd?) -> string
//
// Public API (new):
//   normalizeYmd(input) -> 'YYYYMMDD'
//   formatBatchNo(typePrefix, skuRoot, yyyymmdd, seq) -> string
//   parseBatchNo(batchNo) -> { type, skuRoot, ymd, seq, date }
//   isBatchNoLike(batchNo) -> boolean
//   deriveSkuRootFromRecipe(recipe) -> string
//   deriveSkuRootFromStdItem(item) -> string
//   getNextBatchNoFromRecipe(recipe, { type?, date? }) -> string
//   previewNextBatchNo(typePrefix, skuRoot, dateYmd?) -> string     // NON-RESERVING
//   ensureCounterAtLeast(typePrefix, skuRoot, yyyymmdd, minSeq)
//
// Notes:
// - Accepts legacy & new categories. New: Dry Ingredients, Wet Ingredients, Casings/Clips/Nets,
//   Packaging, Meat, Fish. We map categories → prefixes as follows:
//     • Ingredient-like (Dry/Wet/Casings/Clips/Nets) → INGR
//     • Meat/Fish → MEAT (legacy INGR meat batches still parse OK)
//     • Packaging/Component/Additional → ADD
//     • Products/WIP → PROD
//
// - date inputs may be Date / Firestore Timestamp-like / 'YYYY-MM-DD' / 'YYYYMMDD' / number(ms).
// - SKU root is the first two hyphen groups (e.g. CH-CHOR, SA-SSSG).

import { db } from "../../../services/firebase";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";

/* ---------------------------------- dates ---------------------------------- */

function pad2(n) {
    const s = String(n | 0);
    return s.length === 1 ? "0" + s : s;
}

function pad3(n) {
    const x = Number(n || 0);
    if (x < 10) return `00${x}`;
    if (x < 100) return `0${x}`;
    return String(x);
}

function todayYmd() {
    const d = new Date();
    return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
}

/** Accepts Date | {seconds}|{toDate()} | number(ms) | 'YYYY-MM-DD' | 'YYYYMMDD' | undefined */
export function normalizeYmd(dateLike) {
    // Fast paths for strings
    if (typeof dateLike === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateLike)) return dateLike.replace(/-/g, "");
        if (/^\d{8}$/.test(dateLike)) return dateLike;
    }

    // Firestore Timestamp-like
    if (dateLike && typeof dateLike === "object") {
        try {
            if (typeof dateLike.toDate === "function") {
                const d = dateLike.toDate();
                if (d instanceof Date && !isNaN(d.getTime())) {
                    return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
                }
            }
            if (typeof dateLike.seconds === "number") {
                const d = new Date(dateLike.seconds * 1000);
                return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
            }
        } catch { /* ignore */ }
    }

    // Date or epoch ms
    if (dateLike instanceof Date && !isNaN(dateLike.getTime())) {
        return `${dateLike.getFullYear()}${pad2(dateLike.getMonth() + 1)}${pad2(dateLike.getDate())}`;
    }
    if (typeof dateLike === "number" && Number.isFinite(dateLike)) {
        const d = new Date(dateLike);
        if (!isNaN(d.getTime())) {
            return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
        }
    }

    // Fallback to today
    return todayYmd();
}

export function ymdToDate(yyyymmdd) {
    const m = String(yyyymmdd || "").match(/^(\d{4})(\d{2})(\d{2})$/);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
}

/* ---------------------------------- SKU roots ---------------------------------- */

function sanitizeRootToken(token) {
    return String(token || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, ""); // keep alnum only
}

/** 
 * Take the first two hyphen groups as the root (e.g. CH-CHOR). Fallback to single token or GEN.
 * @deprecated Use unifiedProductService.getSKURoot() instead
 * This function is kept for backward compatibility but will be removed in a future version
 */
export function deriveSkuRoot(codeLike) {
    if (!codeLike) return "GEN";
    const parts = String(codeLike)
        .trim()
        .toUpperCase()
        .replace(/_/g, "-")
        .split("-")
        .filter(Boolean)
        .map(sanitizeRootToken)
        .filter(Boolean);
    if (parts.length >= 2) return `${parts[0]}-${parts[1]}`;
    return parts[0] || "GEN";
}

/** From a recipe: prefer explicit fields; fall back to name. */
export function deriveSkuRootFromRecipe(recipe) {
    if (!recipe) return "GEN";
    const explicit = (recipe.skuRoot || recipe.productCode || recipe.code || recipe.sku || "").toString().trim();
    if (explicit) return deriveSkuRoot(explicit);
    const safeFromName = (recipe.name || "GEN")
        .toString()
        .toUpperCase()
        .replace(/[^A-Z0-9\-]/g, "-")
        .replace(/-+/g, "-");
    return deriveSkuRoot(safeFromName);
}

/** From a catalog/standard item: prefer sku/code; fall back to name. */
export function deriveSkuRootFromStdItem(item) {
    if (!item) return "GEN";
    const explicit = (item.sku || item.code || item.productCode || "").toString().trim();
    if (explicit) return deriveSkuRoot(explicit);
    const safeFromName = (item.name || "GEN")
        .toString()
        .toUpperCase()
        .replace(/[^A-Z0-9\-]/g, "-")
        .replace(/-+/g, "-");
    return deriveSkuRoot(safeFromName);
}

/* ---------------------------------- counters ---------------------------------- */

/**
 * Normalize a variety of category/type hints to our 4 batch families.
 * Accepts legacy values, new categories, and free-text.
 */
function normTypePrefix(type) {
    const t = String(type || "").toUpperCase().trim();

    // Direct family names (already correct)
    if (t === "PROD" || t.startsWith("PROD")) return "PROD";
    if (t === "INGR" || t === "ING" || t.startsWith("INGR")) return "INGR";
    if (t === "MEAT" || t.startsWith("MEAT")) return "MEAT";
    if (t === "ADD" || t.startsWith("ADD")) return "ADD";

    // Legacy domain terms
    if (t.includes("INGREDIENT")) return "INGR";
    if (t.includes("ADDITIONAL") || t.includes("COMPONENT")) return "ADD";
    if (t === "PRODUCT") return "PROD";

    // New domain categories
    if (t.includes("DRY")) return "INGR";                // Dry Ingredients
    if (t.includes("WET")) return "INGR";                // Wet Ingredients
    if (t.includes("CASING") || t.includes("CLIP") || t.includes("NET")) return "INGR"; // inputs used in processing
    if (t.includes("PACKAG")) return "ADD";              // Packaging (tracked separately generally)
    if (t.includes("FISH") || t.includes("SEAFOOD")) return "MEAT";
    if (t.includes("MEAT")) return "MEAT";

    // Default safe family
    return "PROD";
}

function counterDocId(typePrefix, skuRoot, yyyymmdd) {
    const t = normTypePrefix(typePrefix);
    const s = (skuRoot || "GEN").replace(/[^A-Z0-9\-]/gi, "_");
    const d = yyyymmdd || todayYmd();
    return `${t}_${s}_${d}`;
}

/** Transactionally increment counter and return next integer */
async function nextCounterValue(typePrefix, skuRoot, yyyymmdd) {
    const id = counterDocId(typePrefix, skuRoot, yyyymmdd);
    const ref = doc(db, "BatchCounters", id);
    const value = await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) {
            tx.set(ref, { seq: 1, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
            return 1;
        }
        const current = Number(snap.data()?.seq || 0) + 1;
        tx.update(ref, { seq: current, updatedAt: serverTimestamp() });
        return current;
    });
    return value;
}

/** NON-RESERVING: for UI previews only (DO NOT rely for uniqueness) */
export async function previewNextBatchNo(typePrefix, skuRoot, dateYmd) {
    const ymd = normalizeYmd(dateYmd);
    const id = counterDocId(typePrefix, skuRoot, ymd);
    const ref = doc(db, "BatchCounters", id);
    // Hint only (no reservation)
    let seq = 0;
    await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        seq = (snap.exists() ? Number(snap.data()?.seq || 0) : 0) + 1;
    });
    return formatBatchNo(typePrefix, skuRoot, ymd, seq);
}

/** Migration/backfill: ensure a counter is at least minSeq */
export async function ensureCounterAtLeast(typePrefix, skuRoot, yyyymmdd, minSeq) {
    const ymd = normalizeYmd(yyyymmdd);
    const id = counterDocId(typePrefix, skuRoot, ymd);
    const ref = doc(db, "BatchCounters", id);
    await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) {
            tx.set(ref, { seq: Number(minSeq) || 0, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
            return;
        }
        const cur = Number(snap.data()?.seq || 0);
        if (cur < Number(minSeq)) {
            tx.update(ref, { seq: Number(minSeq), updatedAt: serverTimestamp() });
        }
    });
}

/* ---------------------------------- formatting & parsing ---------------------------------- */

export function formatBatchNo(typePrefix, skuRoot, yyyymmdd, seq) {
    const t = normTypePrefix(typePrefix);
    const root = deriveSkuRoot(skuRoot || "GEN");
    const d = yyyymmdd || todayYmd();
    return `${t}-${root}-${d}-${pad3(seq)}`;
}

const BATCH_RE = /^((PROD|INGR|MEAT|ADD))-([A-Z0-9]+-[A-Z0-9]+)-(\d{8})-(\d{3})$/i;

export function isBatchNoLike(s) {
    return BATCH_RE.test(String(s || ""));
}

export function parseBatchNo(batchNo) {
    const m = String(batchNo || "").match(BATCH_RE);
    if (!m) return null;
    const [, type, , skuRoot, ymd, seqStr] = m;
    return {
        type: type.toUpperCase(),
        skuRoot: skuRoot.toUpperCase(),
        ymd,
        seq: Number(seqStr),
        date: ymdToDate(ymd),
    };
}

/* ---------------------------------- existing public API ---------------------------------- */

export async function getNextInternalBatchNo(nameOrCode, skuLike, dateYmd) {
    // Internal ingredient-like batches (includes raw fish/meat under legacy INGR numbering)
    const root = deriveSkuRoot(skuLike || nameOrCode || "GEN");
    const ymd = normalizeYmd(dateYmd);
    const seq = await nextCounterValue("INGR", root, ymd);
    return formatBatchNo("INGR", root, ymd, seq);
}

export async function getNextProductBatchNo(productCode, skuLike, dateYmd) {
    const root = deriveSkuRoot(skuLike || productCode || "GEN");
    const ymd = normalizeYmd(dateYmd);
    const seq = await nextCounterValue("PROD", root, ymd);
    return formatBatchNo("PROD", root, ymd, seq);
}

export async function getNextBatchNo(typePrefix, codeLike, dateYmd) {
    const root = deriveSkuRoot(codeLike || "GEN");
    const ymd = normalizeYmd(dateYmd);
    const seq = await nextCounterValue(typePrefix, root, ymd);
    return formatBatchNo(typePrefix, root, ymd, seq);
}

/* ---------------------------------- recipe-first helpers ---------------------------------- */

export async function getNextBatchNoFromRecipe(recipe, opts = {}) {
    const type = normTypePrefix(opts.type || "PROD");
    const ymd = normalizeYmd(opts.date);
    const root = deriveSkuRootFromRecipe(recipe);
    const seq = await nextCounterValue(type, root, ymd);
    return formatBatchNo(type, root, ymd, seq);
}
